import { defineStore } from 'pinia'
import { useCustomerStore } from '@/stores/customerStore'
import { useDemographicStore } from '@/stores/demographicStore'
import { useFarmStore } from '@/stores/farmStore'
import { useFieldStore } from '@/stores/fieldStore'
import { useEconomicStore } from '@/stores/economicStore'
import { useHarvestStore } from '@/stores/harvestStore'
import { useAgriHarvestStore } from '@/stores/agriHarvestStore'
import { useAgreementTermStore } from '@/stores/agreementTermStore'
import { useAgriProductStore } from '@/stores/agriProductStore'
import { useAgriApplicationStore } from '@/stores/agriApplicationStore'
import { useAgriProductAgriApplicationStore } from '@/stores/agriProductAgriApplicationStore'
import { useAgriOrderStore } from '@/stores/agriOrderStore'
import { useAssessmentStore } from '@/stores/assessmentStore'
import { useCropStore } from '@/stores/cropStore'
import { useSurveyStore } from '@/stores/surveyStore'
import { useSurveyQuestionStore } from '@/stores/surveyQuestionStore'
import { useRecommendationStore } from '@/stores/recommendationStore'
import { useSoilTestStore } from '@/stores/soilTestStore.js'
import { useSurveyResponseStore } from '@/stores/surveyResponseStore'
import { useSurveyResponseAnswerStore } from '@/stores/surveyResponseAnswerStore'
import { useTrainingCourseStore } from '@/stores/trainingCourseStore'
import { useTrainingScheduleStore } from '@/stores/trainingScheduleStore'
import { useTrainingAttendanceStore } from '@/stores/trainingAttendanceStore'
import { useDepotStore } from '@/stores/depotStore'
import { useBusinessStore } from '@/stores/businessStore.js'
import { useAgriSubContractStore } from '@/stores/agriSubContractStore.js'
import { useAgriSubContractStepStore } from '@/stores/agriSubContractStepStore.js'
import { useDeliveryStore } from '@/stores/deliveryStore.js'

import SasagriLogo from '@/assets/header-logos/sasagri.png'
import UltimateLogo from '@/assets/header-logos/ultimate_tz.png'
import HugoLogo from '@/assets/header-logos/hugo.png'
import EmazaoLogo from '@/assets/header-logos/emazao.png'
import AgromaxLogo from '@/assets/header-logos/agromax_logo.jpg'
import Ecolinks from '@/assets/header-logos/ecolinks.jpg'
import onefarmLogo from '@/assets/onefarm.svg'

import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useApiResource } from '@/composables/resources/useApiResource.js'
import { useI18nUtils } from '@/composables/utils/useI18nUtils.js'
import { useDeviceIdentification } from '@/composables/useDeviceIdentification.js'
import { requestPersistentStorage } from '@/helpers/offlineSupport.js'
import { endSession } from '@/helpers/session.js'
import AppConfig from '~/config/appConfig.js'

export const useUserStore = defineStore('userStore', () => {
  const router = useRouter()
  // Wrap in method because useXXXResource references this store. 
  // It will create a initialization loop otherwise
  const resource = () => useApiResource()

  let user = ref(null)
  let partner = ref(null)
  let currentUserMsisdn = ref(null)
  let currentLocale = ref(null)
  let authenticationKey = ref(null)
  let settings = ref(null)
  let country = ref(null)
  let partnerLogo = ref('')
  let currentStoreVersion = ref(0)

  async function requestOTP(msisdn) {
    const payload = await resource().get(`/get_otp`, { msisdn: msisdn })
    currentUserMsisdn.value = msisdn

    authenticationKey.value = payload.data.key
    country.value = payload.data.country

    await router.push({ path: '/auth/validate_otp' })
  }

  async function signIn(userResponse) {
    user.value = userResponse
    await router.push({ path: '/auth/select_partner' })
  }

  async function skipPartnerSelect(userResponse) {
    user.value = userResponse
    await selectPartner(userResponse.partners[0].id)
  }

  async function validateOtp(otp) {
    let params = { key: authenticationKey.value, otp: otp }
    return await resource().post(`/validate_otp`, params)
  }

  async function newOtp() {
    let payload = await resource().get("/get_otp", { msisdn: currentUserMsisdn.value })

    authenticationKey.value = payload.data.key
  }

  // Logout, from whichever screen (helpers/session.js): clears everything of the session from the
  // device and the service worker, and loads the sign-in screen. Resolves once it is cleared.
  function signOut() {
    return endSession()
  }

  async function selectPartner(partnerId) {
    const { setLocale, setPartnerCode } = useI18nUtils()
    const deviceIdentification = useDeviceIdentification()
    const customerStore = useCustomerStore()
    const demographicStore = useDemographicStore()
    const farmStore = useFarmStore()
    const fieldStore = useFieldStore()
    const economicStore = useEconomicStore()
    const harvestStore = useHarvestStore()
    const agriHarvestStore = useAgriHarvestStore()
    const agreementTermStore = useAgreementTermStore()
    const agriProductStore = useAgriProductStore()
    const agriOrderStore = useAgriOrderStore()
    const agriApplicationStore = useAgriApplicationStore()
    const agriProductAgriApplicationStore = useAgriProductAgriApplicationStore()
    const assessmentStore = useAssessmentStore()
    const cropStore = useCropStore()
    const surveyStore = useSurveyStore()
    const surveyQuestionStore = useSurveyQuestionStore()
    const surveyResponseStore = useSurveyResponseStore()
    const surveyResponseAnswerStore = useSurveyResponseAnswerStore()
    const trainingCourseStore = useTrainingCourseStore()
    const trainingScheduleStore = useTrainingScheduleStore()
    const depotStore = useDepotStore()
    const trainingAttendanceStore = useTrainingAttendanceStore()
    const agriSubContractStore = useAgriSubContractStore()
    const agriSubContractStepStore = useAgriSubContractStepStore()
    const businessStore = useBusinessStore()
    const recommendationStore = useRecommendationStore()
    const soilTestStore = useSoilTestStore()
    const deliveryStore = useDeliveryStore()

    const data = await resource().post(`/select_partner`, {
      partner_id: partnerId,
      user_key: user.value.key
    })

    settings.value = data.data.settings
    user.value.fieldAgentId = data.data.user.fieldAgentId
    user.value.name = data.data.user.name
    user.value.uuid = data.data.user.uuid
    partner.value = user.value.partners.find((partner) => partner.id === partnerId)

    await deviceIdentification.identifyDevice()
    requestPersistentStorage()

    currentLocale.value = partner.value.locale
    setLocale(currentLocale.value)
    setPartnerCode(partner.value.group_code)

    if (settings.value.style?.header_color) {
      let root = document.querySelector(':root')
      if (AppConfig.theme !== 'sbg') {
        root.style.setProperty('--header-color', settings.value.style?.header_color)
        root.style.setProperty('--header-text-color', settings.value.style?.header_text_color)
        root.style.setProperty('--footer-icon-color', settings.value.style?.footer_icon_color)
        root.style.setProperty('--footer-active-icon-color', settings.value.style?.footer_active_icon_color)
      }

      localStorage.setItem('header-color', settings.value.style?.header_color)
      localStorage.setItem('header-text-color', settings.value.style?.header_text_color)
      localStorage.setItem('footer-icon-color', settings.value.style?.footer_icon_color)
      localStorage.setItem('footer-active-icon-color', settings.value.style?.footer_active_icon_color)
    }

    switch (partner.value.code) {
      case 'nile_ug':
      case 'dairy_ke':
      case 'dairydev_ke':
      case 'ultimate_tz':
      case 'chm_ug':
      case 'chmdev_ug':
        partnerLogo.value = UltimateLogo
        break

      case 'apex_tz':
      case 'unada_ug':
      case 'unadadev_ug':
        partnerLogo.value = EmazaoLogo
        break

      case 'agromax_ug':
        partnerLogo.value = AgromaxLogo
        break

      default:
        switch (partner.value.group_code) {
          case 'ecolinks':
            partnerLogo.value = Ecolinks
            break

          // Hugo Restrepo's partners (hr_co, hrdev_co, hr_pe, hrdev_pe) share the group code
          case 'hr':
            partnerLogo.value = HugoLogo
            break
        }
    }

    if(!partnerLogo.value) {
      if(AppConfig.theme === 'sbg') {
        partnerLogo.value = onefarmLogo
      } else {
        partnerLogo.value = SasagriLogo
      }
    }

    const storesToSync = [
      agreementTermStore,
      agriApplicationStore,
      agriOrderStore,
      agriProductAgriApplicationStore,
      agriProductStore,
      agriSubContractStore,
      agriSubContractStepStore,
      assessmentStore,
      businessStore,
      cropStore,
      customerStore,
      deliveryStore,
      demographicStore,
      depotStore,
      economicStore,
      farmStore,
      fieldStore,
      harvestStore,
      agriHarvestStore,
      recommendationStore,
      soilTestStore,
      surveyQuestionStore,
      surveyResponseAnswerStore,
      surveyResponseStore,
      surveyStore,
      trainingAttendanceStore,
      trainingCourseStore,
      trainingScheduleStore,
    ]

    for (const store of storesToSync) {
      try {
        await store.getCollection()
      }
      catch (err) {
        // Ignored. The backend should log what went wrong  
      }
    }

    if ((partner.value.code == 'unada_ug' || partner.value.code === 'unadadev_ug') && user.value.sign_in_count == 1) {
      await router.push({ path: '/first_sign_in' })
    }
    else {
      await router.push({ path: '/' })
    }
  }

  function currentUser() {
    return user.value.id
  }

  function currentPartner() {
    return partner.value
  }

  async function fetchAccountDetails() {
    let customerStore = useCustomerStore()
    if (user.value && user.value.uuid) {
      let response = await resource().get(`/customers/${user.value.uuid}`)
      customerStore.customer = response.data
    }
  }

  function $reset() {
    user.value = null
    partner.value = null
    currentUserMsisdn.value = null
    authenticationKey.value = null
    settings.value = null
    country.value = null
    partnerLogo.value = ''
  }

  return {
    user,
    partner,
    currentUserMsisdn,
    currentLocale,
    authenticationKey,
    settings,
    country,
    partnerLogo,
    currentStoreVersion,
    requestOTP,
    signIn,
    skipPartnerSelect,
    validateOtp,
    newOtp,
    selectPartner,
    signOut,
    currentUser,
    currentPartner,
    fetchAccountDetails,
    $reset
  }

}, {
  persist: true
})